// Re-computes the per-variable risk terms behind an assessment score, using the
// same formulas as @roadscience/algorithms (polymer.js, rs1.js, rejuv.js and the
// asphalt algorithms), so the Cold / Humid / Rain labels can only appear when that
// variable actually contributes to the score the colour is based on.
//
// Every score is  temp + (1 - humid) + (1 - rain)  (asphalt: temp + (1 - rain)),
// where each term is acos(x)/pi with the real part taken: x > 1 gives 0, x < -1 gives 1.
// The helpers below return those contributions, 0 meaning "no risk from this variable".

const mean = (values) => values.reduce((total, value) => total + value, 0) / values.length
const sum = (values) => values.reduce((total, value) => total + value, 0)

const realAcosOverPi = (x) => {
  if (x > 1) return 0
  if (x < -1) return 1

  return Math.acos(x) / Math.PI
}

const POLY_RATE_VALUE = { '1L': 800, '2L': 1200, '3L': 1500 }

const RS1_RATE_VALUE = {
  STD: { '1L': 4.3, '2L': 7.7, '3L': 9 },
  WBK0: { '1L': 11 / 3, '2L': 28 / 4, '3L': 36 / 4 },
  WBK2: { '1L': 5 / 2, '2L': 8 / 2, '3L': 10 / 2 },
  WBPLUSSEAL: { '1L': 11 / 3, '2L': 19 / 3, '3L': 25 / 3 },
  WBTACKCOAT: { '1L': 14 / 7, '2L': 19 / 3, '3L': 25 / 3 },
}

const RS1_TEMPERATURE_SLOPE = {
  STD: () => 2 / 3,
  WBK0: () => 3 / 4,
  WBK2: () => 2 / 5,
  WBPLUSSEAL: () => 2 / 3,
  WBTACKCOAT: (rate) => (rate === '1L' ? 2 / 7 : 2 / 3),
}

const RS1_WEATHER_BARRIER = ['WBK0', 'WBK2', 'WBPLUSSEAL', 'WBTACKCOAT']

const toRiskContributions = ({ temp, humid, rainfall }) => ({
  temperatureFeelLike: temp,
  relativeHumidity: humid === undefined ? 0 : 1 - humid,
  quantitativeRainfall: 1 - rainfall,
})

function polymer({ temperature, humidity, rainfall, rate }) {
  const constant = POLY_RATE_VALUE[rate]
  if (constant === undefined) return null

  return toRiskContributions({
    temp: realAcosOverPi(100 * temperature - constant),
    humid: realAcosOverPi(10 * humidity - 800),
    rainfall: realAcosOverPi(10 * rainfall - 1.5),
  })
}

function rs1({ temperature, humidity, rainfall, rate, type }) {
  const algorithmType = RS1_RATE_VALUE[type] ? type : 'STD'
  const constant = RS1_RATE_VALUE[algorithmType][rate]
  if (constant === undefined) return null

  const slope = RS1_TEMPERATURE_SLOPE[algorithmType](rate)
  const humidTerm = RS1_WEATHER_BARRIER.includes(algorithmType)
    ? realAcosOverPi(humidity / 10 - 9.5)
    : realAcosOverPi(humidity / 5 - 17)

  return toRiskContributions({
    temp: realAcosOverPi(slope * temperature - constant),
    humid: humidTerm,
    rainfall: realAcosOverPi(10 * rainfall - 1.5),
  })
}

function rejuv({ temperature, humidity, rainfall }) {
  const temp = (6.65 * Math.atan(((7 / 50) * temperature - 1.9) ** 2) - (4 * Math.PI) / 3) / Math.PI

  return toRiskContributions({
    temp,
    humid: realAcosOverPi((2 / 5) * humidity - 35),
    rainfall: realAcosOverPi(10 * rainfall - 1.5),
  })
}

function asphalt({ temperature, rainfall, type }) {
  const constant = { STRUCTURAL: 500, SURFACING: 1000 }[type]
  if (constant === undefined) return null

  return toRiskContributions({
    temp: realAcosOverPi(100 * temperature - constant),
    rainfall: realAcosOverPi(5 * rainfall - 1),
  })
}

const ASSESSORS = {
  POLY: polymer,
  RS1: rs1,
  REJUV: rejuv,
  ASPHALT: asphalt,
}

const hasValues = (values) => Array.isArray(values) && values.length > 0

// Returns how much each variable adds to the risk score (0 = none), or null when
// the algorithm is not one we can reproduce (the caller then falls back to the
// generic thresholds).
function getRiskContributions(product, algorithmInputs = {}) {
  if (!product || !product.applicableAlgorithm) return null

  const [algorithm = '', , type] = product.applicableAlgorithm.split(':')
  const assess = ASSESSORS[algorithm.toUpperCase()]
  if (!assess) return null

  const { temperatureFeelLike, relativeHumidity, quantitativeRainfall } = algorithmInputs
  if (!hasValues(temperatureFeelLike) || !hasValues(quantitativeRainfall)) return null

  const contributions = assess({
    temperature: mean(temperatureFeelLike),
    humidity: hasValues(relativeHumidity) ? mean(relativeHumidity) : undefined,
    rainfall: sum(quantitativeRainfall),
    rate: product.applicationRate,
    type: type && type.toUpperCase(),
  })

  if (!contributions) return null

  const values = Object.values(contributions)

  return values.every(Number.isFinite) ? contributions : null
}

export { getRiskContributions }
